import React, { useState, useRef, useEffect } from 'react';
import { IconX } from './icons';

export interface Tab {
  id: string;
  name: string;
  isActive: boolean;
}

interface TabBarProps {
  tabs: Tab[];
  onTabClick: (tabId: string) => void;
  onTabRename?: (tabId: string, newName: string) => void;
  onTabClose?: (tabId: string) => void;
  onTabReorder?: (fromIndex: number, toIndex: number) => void;
  onAddTab?: () => void;
}

export const TabBar: React.FC<TabBarProps> = ({
  tabs,
  onTabClick,
  onTabRename,
  onTabClose,
  onTabReorder,
  onAddTab,
}) => {
  const [editingTabId, setEditingTabId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when editing starts
  useEffect(() => {
    if (editingTabId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingTabId]);

  const handleDoubleClick = (tab: Tab) => {
    if (onTabRename) {
      setEditingTabId(tab.id);
      setEditingValue(tab.name);
    }
  };

  const handleRenameSubmit = () => {
    if (editingTabId && editingValue.trim() && onTabRename) {
      onTabRename(editingTabId, editingValue.trim());
    }
    setEditingTabId(null);
    setEditingValue('');
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleRenameSubmit();
    } else if (e.key === 'Escape') {
      setEditingTabId(null);
      setEditingValue('');
    }
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    if (onTabReorder) {
      setDraggedIndex(index);
      e.dataTransfer.effectAllowed = 'move';
    }
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragEnd = () => {
    if (draggedIndex !== null && dragOverIndex !== null && draggedIndex !== dragOverIndex && onTabReorder) {
      onTabReorder(draggedIndex, dragOverIndex);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <div className="tab-bar">
      <div className="tab-bar-container">
        <div className="tab-bar-tabs">
          {tabs.map((tab, index) => {
            const isEditing = editingTabId === tab.id;
            const isDragging = draggedIndex === index;
            const isDragOver = dragOverIndex === index;

            return (
              <div
                key={tab.id}
                className={`tab ${tab.isActive ? 'tab-active' : ''} ${isDragging ? 'tab-dragging' : ''} ${isDragOver ? 'tab-drag-over' : ''}`}
                onClick={() => !isEditing && onTabClick(tab.id)}
                onDoubleClick={() => handleDoubleClick(tab)}
                draggable={onTabReorder && !isEditing}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
              >
                {isEditing ? (
                  <input
                    ref={inputRef}
                    type="text"
                    className="tab-input"
                    value={editingValue}
                    onChange={(e) => setEditingValue(e.target.value)}
                    onBlur={handleRenameSubmit}
                    onKeyDown={handleRenameKeyDown}
                  />
                ) : (
                  <>
                    <span className="tab-name">{tab.name}</span>
                    {onTabClose && tabs.length > 1 && (
                      <button
                        className="tab-close"
                        onClick={(e) => {
                          e.stopPropagation();
                          onTabClose(tab.id);
                        }}
                        aria-label={`Close ${tab.name}`}
                      >
                        <IconX className="w-3 h-3" />
                      </button>
                    )}
                  </>
                )}
              </div>
            );
          })}

          {onAddTab && (
            <button className="tab-add" onClick={onAddTab} aria-label="Add new tab">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
